import { createRootRouteWithContext, Outlet } from '@tanstack/react-router'
import { MsalAuthenticationTemplate } from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { TanStackRouterDevtools } from '@tanstack/router-devtools'
import { QueryClient } from '@tanstack/react-query'
import { LoginRequest } from '../config/authconfig'
import { AppShellLayout } from '../components/layout'
import { useEffect } from 'react'
import { enhancedSessionTracking, smartSessionTracking } from '../api/sessiontracking'
import { useUserInfo } from '../hooks/useUserInfo'
import { useUserSettingsStore } from '../stores/userSettingsStore'
import { useAppVersionCheck } from '../hooks/useAppVersionCheck'

interface RouterContext {
  queryClient: QueryClient;
}

declare global {
    interface Window {
        sessionTrackingCalled?: boolean;
        lastSessionTrackingTime?: number;
        isSmartTrackingInProgress?: boolean;
        currentSessionId?: string;
    }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: () => <div>404 - Page Not Found</div>,
})

function RootComponent() {
  const authRequest = { ...LoginRequest }
  const { isAuthenticated } = useUserInfo()
  const { loadUserSettings } = useUserSettingsStore()

  useAppVersionCheck({ isAuthenticated, runInDev: true })

  // Load user settings when authentication completes
  useEffect(() => {
    if (isAuthenticated) {
      console.log('User authenticated, loading user settings');
      loadUserSettings().catch((error) => {
        console.error('Failed to load user settings after authentication:', error);
      });
    }
  }, [isAuthenticated, loadUserSettings]);

  // Session tracking - runs once after authentication is complete
  // Note: Additional session tracking calls are handled in auth.ts for token refresh events
  useEffect(() => {
    // Only run session tracking when user is authenticated
    if (!isAuthenticated) {
      return;
    }

    const trackSession = async () => {
      // Check if session tracking has already been called globally
      if (window.sessionTrackingCalled) {
        console.log("Session tracking already called, skipping");
        return;
      }

      // Set flag immediately to prevent multiple calls from other components
      window.sessionTrackingCalled = true;
      console.log("Setting sessionTrackingCalled flag to true");
      
      try {
        // Use enhancedSessionTracking for initial call to set timestamp
        console.log("Calling enhancedSessionTracking for initial session");
        await enhancedSessionTracking();
        console.log("Initial session tracking completed");
      } catch (error) {
        console.error("Failed to track initial session:", error);
        // Reset flag on error so it can be retried
        window.sessionTrackingCalled = false;
        console.log("Reset sessionTrackingCalled flag due to error");
      }
    };

    trackSession();
  }, [isAuthenticated]);

  // Handle visibility change to detect when user returns to tab
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log("User returned to tab, checking if session tracking is needed");
        
        // Check if we recently tracked a session (within last 1 hour)
        if (window.lastSessionTrackingTime) {
          const timeSinceLastTrack = Date.now() - window.lastSessionTrackingTime;
          const ONE_HOUR = 60 * 60 * 1000;
          if (timeSinceLastTrack < ONE_HOUR) {
            console.log("Session tracked recently (", Math.round(timeSinceLastTrack / (60 * 1000)), "minutes ago), skipping visibility change tracking");
            return;
          }
        }
        
        try {
          // Use smart session tracking which checks token expiration
          await smartSessionTracking();
        } catch (error) {
          console.error("Failed to track session on visibility change:", error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Periodic session tracking to ensure continuous tracking
  // Note: Uses smart tracking which checks token expiration to avoid excessive records
  useEffect(() => {
    if (!isAuthenticated) return;

    const TWELVE_HOURS = 12 * 60 * 60 * 1000; // Check every 12 hours as a safety net
    
    const periodicTracking = async () => {
      try {
        await smartSessionTracking();
        console.log("Periodic session tracking completed");
      } catch (error) {
        console.error("Failed to complete periodic session tracking:", error);
      }
    };

    // Set up interval for periodic tracking
    const intervalId = setInterval(periodicTracking, TWELVE_HOURS);
    
    // Clean up interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  return (
    <MsalAuthenticationTemplate 
      authenticationRequest={authRequest} 
      interactionType={InteractionType.Redirect}
    >
      <AppShellLayout>
        <Outlet />
      </AppShellLayout>
      {/* <TanStackRouterDevtools position="bottom-right" /> */}
    </MsalAuthenticationTemplate>
  )
}
